import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { gridPlacement } from '../../lib/miniature-grid.js';

/**
 * A miniature drawn on the grid it will occupy at the table.
 *
 * Miniature art may overflow the cells the miniature occupies — the PSD canvas is
 * the footprint plus a one-cell ring, and artists are told they may spill into it.
 * Shown alone in a fixed box, a piece that overhangs by a whole cell looks exactly
 * like one that sits neatly inside its footprint. On a grid, it doesn't.
 *
 * Everything is centered on the grid center, which IS the footprint center — the
 * contract `cropSquareCentered` guarantees for the images it produces. How wide to
 * draw the art (and whether the image is one of those crops at all) is
 * `gridPlacement`'s call; see it for the images that aren't.
 *
 * @property {string} src           Image URL (the flattened miniature).
 * @property {number} footprintCells  Cells the miniature occupies (0 = unknown —
 *   nothing to mark, so the grid renders without a footprint square).
 * @property {number} canvasCells   Cells of authoring canvas for the size; the grid
 *   is at least this wide, so "room available vs room used" stays legible.
 * @property {number} cellPx        Source pixels per grid cell (the api's cell size).
 * @property {number} cellSize      Rendered px per cell.
 */
class MiniatureGridPreview extends BaseComponent {
    static properties = {
        src: { type: String },
        footprintCells: { type: Number, attribute: 'footprint-cells' },
        canvasCells: { type: Number, attribute: 'canvas-cells' },
        cellPx: { type: Number, attribute: 'cell-px' },
        cellSize: { type: Number, attribute: 'cell-size' },
        _imageCells: { type: Number, state: true },
        _gridCells: { type: Number, state: true },
    };

    static styles = css`
        :host { display: inline-block; }
        .stage {
            position: relative;
            background-color: #fbfbfa;
            background-image:
                linear-gradient(to right, #d9dde3 1px, transparent 1px),
                linear-gradient(to bottom, #d9dde3 1px, transparent 1px);
            background-position: 0 0;
            border: 1px solid #adb5bd;
            border-radius: 4px;
        }
        /* The footprint: the cells the miniature actually occupies. Distinct from
           the cell rules so it doesn't read as just another grid line. */
        .footprint {
            position: absolute;
            border: 2px solid rgba(60, 80, 130, 0.85);
            background: rgba(60, 80, 130, 0.06);
            pointer-events: none;
        }
        img {
            position: absolute;
            display: block;
            image-rendering: auto;
            pointer-events: none;
        }
    `;

    constructor() {
        super();
        this.src = '';
        this.footprintCells = 0;
        this.canvasCells = 0;
        this.cellPx = 0;
        this.cellSize = 48;
        // Until the image loads its extent is unknown: the grid renders at the
        // size the miniature is known to need, with no art on it yet.
        this._imageCells = 0;
        this._gridCells = 0;
    }

    /** The placement is pure arithmetic and lives in lib, where it is tested. */
    _handleLoad(e) {
        const placement = gridPlacement({
            naturalWidth: e.target.naturalWidth,
            cellPx: this.cellPx,
            footprintCells: this.footprintCells,
            canvasCells: this.canvasCells,
        });
        this._imageCells = placement.imageCells;
        this._gridCells = placement.gridCells;
    }

    render() {
        const cells = Math.max(this._gridCells, this.canvasCells, this.footprintCells, 1);
        const side = cells * this.cellSize;
        // Everything centers on the grid center, which IS the footprint center —
        // that is the contract the crop guarantees.
        const footprintSide = this.footprintCells * this.cellSize;
        const imageSide = this._imageCells * this.cellSize;
        return html`
            <div class="stage" style="width:${side}px; height:${side}px; background-size:${this.cellSize}px ${this.cellSize}px;">
                ${
                    this.footprintCells > 0
                        ? html`<div class="footprint" style="
                            width:${footprintSide}px; height:${footprintSide}px;
                            left:${(side - footprintSide) / 2}px; top:${(side - footprintSide) / 2}px;"></div>`
                        : ''
                }
                <img src="${this.src}" alt="" @load="${this._handleLoad}" style="
                    width:${imageSide}px; height:${imageSide}px;
                    left:${(side - imageSide) / 2}px; top:${(side - imageSide) / 2}px;">
            </div>
        `;
    }
}

customElements.define('miniature-grid-preview', MiniatureGridPreview);
